import Tippy from '@tippyjs/react';
import { t } from 'i18next';
import React, { useState, useRef, useEffect, ReactNode } from 'react';

type SupportedTags =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'div'
  | 'span';
type ButtonPosition = 'left' | 'right' | 'center';

interface TruncateTextProps {
  tag?: SupportedTags;
  children: ReactNode;
  maxLines?: number;
  maxCharacters?: number;
  className?: string;
  showTooltip?: boolean;
  showExpandButton?: boolean;
  buttonPosition?: ButtonPosition;
  charCountForNotShowingButtons?: number;
  dangerouslySetInnerHTML?: boolean;
  id?: string;
  buttonClassName?: string;
  textTooltip?: string;
  contentEditable?: boolean;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  role?: string;
  tabIndex?: number;
  dir?: string;
  suppressContentEditableWarning?: boolean;
  style?: React.CSSProperties;
  expandOnFocus?: boolean;
}

const TruncateText: React.FC<TruncateTextProps> = ({
  tag = 'p',
  children,
  maxLines = 2,
  maxCharacters,
  className = '',
  showTooltip = false,
  showExpandButton = false,
  charCountForNotShowingButtons = 180,
  dangerouslySetInnerHTML = false,
  buttonClassName = '',
  textTooltip,
  contentEditable,
  onBlur,
  onFocus,
  role,
  tabIndex,
  dir,
  suppressContentEditableWarning,
  style,
  id,
  expandOnFocus = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get text content for character count check
  const getTextContent = (): string => {
    if (textTooltip) return textTooltip;
    if (typeof children === 'string') return children;
    if (dangerouslySetInnerHTML && typeof children === 'string') {
      // Strip HTML tags for character count
      return children.replace(/<[^>]*>/g, '');
    }
    return '';
  };

  const textContent = getTextContent();

  const truncatedContent =
    maxCharacters &&
    !isExpanded &&
    !isEditing &&
    textContent.length > maxCharacters
      ? `${textContent.slice(0, maxCharacters)}...`
      : children;

  const shouldShowButton =
    showExpandButton &&
    !isEditing &&
    (textContent.length > charCountForNotShowingButtons ||
      needsTruncation ||
      (maxCharacters && textContent.length > maxCharacters));

  // Check if content needs truncation
  useEffect(() => {
    const checkTruncation = (): void => {
      if (!textRef.current || isExpanded || isEditing) return;

      if (maxCharacters && textContent.length > maxCharacters) {
        setNeedsTruncation(true);
        return;
      }

      const element = textRef.current;

      if (maxLines === 1) {
        // Single line: check horizontal overflow
        setNeedsTruncation(element.scrollWidth > element.clientWidth);
      } else {
        // Multiple lines: check if content exceeds maxLines height
        const styles = window.getComputedStyle(element);
        const lineHeight =
          parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;

        // Get the precise height including line breaks
        const range = document.createRange();
        range.selectNodeContents(element);
        const contentHeight = range.getBoundingClientRect().height;

        // Calculate actual lines considering line breaks
        const actualLines = Math.round(contentHeight / lineHeight);

        // Only set truncation if actual lines are strictly greater than maxLines
        setNeedsTruncation(actualLines > maxLines);
      }
    };

    let resizeObserver: ResizeObserver | null = null;

    if (!isEditing && window.ResizeObserver && containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(checkTruncation);
      });
      resizeObserver.observe(containerRef.current);
    }

    const timeoutId = setTimeout(checkTruncation, 0);

    return (): void => {
      clearTimeout(timeoutId);
      resizeObserver?.disconnect();
    };
  }, [
    maxLines,
    maxCharacters,
    children,
    isExpanded,
    isEditing,
    textContent.length,
  ]);

  const getTextStyles = (): React.CSSProperties => {
    const baseStyles = style || {};

    if (isExpanded || isEditing) {
      return {
        ...baseStyles,
        display: 'block',
        whiteSpace: 'pre-wrap',
        overflow: 'visible',
        lineHeight: '1.5',
        wordBreak: 'break-word',
        hyphens: 'auto',
      };
    }

    if (maxLines === 1) {
      return {
        ...baseStyles,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        // maxWidth: '100%',
        maxWidth: 'calc(var(--unit-minthr) * 200)',
        display: 'block',
      };
    }

    return {
      ...baseStyles,
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      lineHeight: '1.5',
      wordBreak: 'break-word',
      hyphens: 'auto',
    };
  };

  interface TagComponentProps {
    children: ReactNode;
    className?: string;
    style?: React.CSSProperties;
    contentEditable?: boolean;
    onBlur?: React.FocusEventHandler<HTMLElement>;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    role?: string;
    tabIndex?: number;
    dir?: string;
    suppressContentEditableWarning?: boolean;
    id?: string;
    onPaste?: React.ClipboardEventHandler<HTMLElement>;
  }

  const TagComponent = React.forwardRef<HTMLElement, TagComponentProps>(
    ({ children: tagChildren, ...props }, ref) => {
      const tagMap = {
        h1: 'h1',
        h2: 'h2',
        h3: 'h3',
        h4: 'h4',
        h5: 'h5',
        h6: 'h6',
        p: 'p',
        div: 'div',
        span: 'span',
      };

      const Component = tagMap[tag] || 'p';
      return React.createElement(Component, { ...props, ref }, tagChildren);
    },
  );

  TagComponent.displayName = 'TagComponent';

  const renderContent = (): ReactNode => {
    if (dangerouslySetInnerHTML && typeof children === 'string') {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html:
              maxCharacters && !isExpanded && !isEditing
                ? (truncatedContent as string)
                : children,
          }}
        />
      );
    }
    return truncatedContent;
  };

  const handleFocus: React.FocusEventHandler<HTMLElement> = (e) => {
    setIsEditing(true);
    if (expandOnFocus && !isExpanded) setIsExpanded(true);
    onFocus?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    setIsEditing(false);
    if (expandOnFocus) setIsExpanded(false);
    onBlur?.(e);
  };

  const handlePaste: React.ClipboardEventHandler<HTMLElement> = (e) => {
    if (!contentEditable) return;
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (document.queryCommandSupported?.('insertText')) {
      document.execCommand('insertText', false, text);
    } else {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      selection.deleteFromDocument();
      const range = selection.getRangeAt(0);
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const textElement =
    shouldShowButton && needsTruncation ? (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <TagComponent
            ref={textRef}
            className={className}
            style={getTextStyles()}
            contentEditable={contentEditable}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onPaste={handlePaste}
            role={role}
            tabIndex={tabIndex}
            dir={dir}
            suppressContentEditableWarning={suppressContentEditableWarning}
            id={id}
          >
            {renderContent()}
          </TagComponent>
          {shouldShowButton && needsTruncation && !isExpanded && (
            <a
              type="button"
              className={buttonClassName}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              style={{
                flexShrink: 0,
                display: 'inline-block',
              }}
            >
              {t('See.more')}
            </a>
          )}
        </div>
        {shouldShowButton && needsTruncation && isExpanded && (
          <a
            type="button"
            className={buttonClassName}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{
              flexShrink: 0,
              display: 'inline-block',
            }}
          >
            {t('See.less')}
          </a>
        )}
      </>
    ) : (
      <TagComponent
        ref={textRef}
        className={className}
        style={getTextStyles()}
        contentEditable={contentEditable}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onPaste={handlePaste}
        role={role}
        tabIndex={tabIndex}
        dir={dir}
        suppressContentEditableWarning={suppressContentEditableWarning}
        id={id}
      >
        {renderContent()}
      </TagComponent>
    );

  const tooltipContent = typeof children === 'string' ? children : textContent;

  return (
    <div className="relative w-full" ref={containerRef}>
      {showTooltip && needsTruncation && !isExpanded && !isEditing ? (
        <Tippy content={tooltipContent}>{textElement}</Tippy>
      ) : (
        textElement
      )}
    </div>
  );
};

export default TruncateText;
